import React, { useState, useEffect, ReactNode } from 'react';
import { Trophy, Medal, Award, Briefcase, Gift, Sparkles } from 'lucide-react';

interface PrizeCardProps {
  rank: string;
  amount: string;
  icon: ReactNode;
  delay: number;
  isWinner: boolean;
}

interface PerkCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  delay: number;
}

const PrizeCard: React.FC<PrizeCardProps> = ({ rank, amount, icon, delay, isWinner }) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`relative transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`relative bg-gradient-to-br from-gray-900 to-black border-2 rounded-2xl p-8 transition-all duration-500 overflow-hidden ${
        isHovered 
          ? 'border-red-500 shadow-2xl shadow-red-500/40 scale-105' 
          : 'border-red-900/30 scale-100'
      }`}>
        {/* Animated background gradient */}
        <div className={`absolute inset-0 bg-gradient-to-br from-red-600/10 to-red-900/5 transition-opacity duration-500 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`} />
        
        {/* Glow effect */}
        <div className={`absolute -top-20 -right-20 w-40 h-40 bg-red-500 rounded-full blur-3xl transition-opacity duration-500 ${
          isHovered ? 'opacity-30' : 'opacity-0'
        }`} />

        {/* Icon with rotation animation */}
        <div className="relative flex justify-center mb-6">
          <div className={`w-28 h-28 rounded-full flex items-center justify-center transition-all duration-500 ${
            isHovered ? 'rotate-12 scale-110' : 'rotate-0 scale-100'
          } ${isWinner ? 'bg-gradient-to-br from-red-500 via-red-600 to-red-700' : 'bg-gradient-to-br from-red-800 via-red-900 to-black'} shadow-2xl relative border-2 ${isWinner ? 'border-red-400' : 'border-red-800'}`}>
            {/* Inner glow ring */}
            <div className={`absolute inset-2 rounded-full ${isWinner ? 'bg-gradient-to-br from-red-400 to-red-600' : 'bg-gradient-to-br from-red-700 to-red-900'} opacity-50 blur-md`} />
            
            <div className={`relative transition-transform duration-500 ${isHovered ? 'scale-125' : 'scale-100'}`}>
              {icon}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative text-center">
          <h3 className="text-2xl font-bold text-white mb-2">{rank}</h3>
          <p className="text-gray-400 text-sm mb-4">Per Problem Statement</p>
          <div className={`text-5xl font-bold transition-all duration-500 ${
            isHovered ? 'scale-110' : 'scale-100'
          } ${isWinner ? 'bg-gradient-to-r from-red-400 via-red-500 to-red-600' : 'bg-gradient-to-r from-red-300 via-red-400 to-red-500'} bg-clip-text text-transparent drop-shadow-lg`}>
            {amount}
          </div>
        </div>

        {/* Sparkle effects on hover */}
        {isHovered && (
          <>
            <Sparkles className="absolute top-6 left-6 w-4 h-4 text-red-400 animate-pulse" />
            <Sparkles className="absolute bottom-6 right-6 w-4 h-4 text-red-400 animate-pulse" style={{ animationDelay: '0.2s' }} />
            <Sparkles className="absolute top-16 right-10 w-3 h-3 text-red-500 animate-pulse" style={{ animationDelay: '0.4s' }} />
          </>
        )}

        {/* Particle effect on hover */}
        {isHovered && (
          <>
            <div className="absolute top-10 left-10 w-2 h-2 bg-red-500 rounded-full animate-ping" />
            <div className="absolute bottom-10 right-10 w-2 h-2 bg-red-500 rounded-full animate-ping" style={{ animationDelay: '0.2s' }} />
            <div className="absolute top-20 right-20 w-2 h-2 bg-red-500 rounded-full animate-ping" style={{ animationDelay: '0.4s' }} />
          </>
        )}
      </div>
    </div>
  );
};

const PerkCard: React.FC<PerkCardProps> = ({ icon, title, description, delay }) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`relative bg-gradient-to-br from-gray-900 to-black border-2 rounded-xl p-6 transition-all duration-500 overflow-hidden ${
        isHovered 
          ? 'border-red-500 shadow-xl shadow-red-500/30 scale-105' 
          : 'border-red-900/30 scale-100'
      }`}>
        {/* Background gradient on hover */}
        <div className={`absolute inset-0 bg-gradient-to-br from-red-500/10 to-transparent transition-opacity duration-500 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`} />

        <div className="relative flex items-start gap-4">
          {/* Icon */}
          <div className={`flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center transition-all duration-500 ${
            isHovered ? 'rotate-6 scale-110' : 'rotate-0 scale-100'
          } shadow-lg shadow-red-500/30 border-2 border-red-500/30`}>
            <div className={`transition-transform duration-500 ${isHovered ? 'scale-125' : 'scale-100'}`}>
              {icon}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
            <p className="text-gray-400 leading-relaxed">{description}</p>
          </div>
        </div>

        {/* Corner accent */}
        <div className={`absolute -bottom-2 -right-2 w-20 h-20 bg-gradient-to-br from-red-500 to-transparent rounded-full blur-2xl transition-opacity duration-500 ${
          isHovered ? 'opacity-40' : 'opacity-0'
        }`} />

        {/* Sparkles on hover */}
        {isHovered && (
          <>
            <Sparkles className="absolute top-4 right-4 w-4 h-4 text-red-400 animate-pulse" />
            <Sparkles className="absolute bottom-4 left-4 w-3 h-3 text-red-400 animate-pulse" style={{ animationDelay: '0.3s' }} />
          </>
        )}
      </div>
    </div>
  );
};

export default function PrizesRewards() {
  const [titleVisible, setTitleVisible] = useState<boolean>(false);

  useEffect(() => {
    setTitleVisible(true);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white p-8 py-16">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
        }`}>
          <h1 className="text-6xl font-bold mb-4">
            <span className="text-white">Prizes </span>
            <span className="bg-gradient-to-r from-red-400 via-red-500 to-red-600 bg-clip-text text-transparent">
              & Rewards
            </span>
          </h1>
          <p className="text-xl text-gray-400 italic max-w-3xl mx-auto mt-6">
            Win cash prizes and unlock prestigious career opportunities across Generative AI, Agentic AI, and AIoT.
          </p>
          
          {/* Total Prize Pool */}
          <div className={`mt-10 inline-block transition-all duration-1000 delay-300 ${
            titleVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}>
            <div className="relative bg-gradient-to-r from-red-900/30 to-black border-2 border-red-500/50 rounded-2xl px-8 py-4 shadow-xl shadow-red-500/30 overflow-hidden">
              {/* Animated background shine */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-red-500/10 to-transparent animate-pulse" />
              
              <div className="relative">
                <p className="text-lg text-gray-300 mb-1">Total Prize Pool</p>
                <p className="text-5xl font-bold bg-gradient-to-r from-red-400 via-red-500 to-red-600 bg-clip-text text-transparent">
                  ₹30,000
                </p>
                <p className="text-sm text-gray-400 mt-1">Across All Domains</p>
              </div>
              
              <Trophy className="absolute -top-2 -right-2 w-16 h-16 text-red-500/10 rotate-12" />
            </div>
          </div>
        </div>

        {/* Prize Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <PrizeCard
            rank="Winner"
            amount="₹15,000"
            icon={<Trophy size={56} className="text-white" />}
            delay={200}
            isWinner={true}
          />
          <PrizeCard
            rank="1st Runner-Up"
            amount="₹10,000"
            icon={<Medal size={56} className="text-red-200" />}
            delay={400}
            isWinner={false}
          />
          <PrizeCard
            rank="2nd Runner-Up"
            amount="₹5,000"
            icon={<Award size={56} className="text-red-300" />}
            delay={600}
            isWinner={false}
          />
        </div>

        {/* Perks Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <PerkCard
            icon={<Briefcase size={32} className="text-white" />}
            title="Internship Opportunities"
            description="Top performers unlock exclusive internship or interview opportunities from our partners."
            delay={800}
          />
          <PerkCard
            icon={<Gift size={32} className="text-white" />}
            title="Additional Perks"
            description="E-certificates for all valid submissions; goodies and on-stage recognition for all shortlisted teams."
            delay={1000}
          />
        </div>

        {/* Floating particles animation */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-red-500 rounded-full animate-ping" style={{ animationDuration: '3s' }} />
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-red-500 rounded-full animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }} />
          <div className="absolute bottom-1/4 left-1/3 w-1 h-1 bg-red-500 rounded-full animate-ping" style={{ animationDuration: '5s', animationDelay: '2s' }} />
          <div className="absolute top-2/3 right-1/4 w-1 h-1 bg-red-500 rounded-full animate-ping" style={{ animationDuration: '4.5s', animationDelay: '1.5s' }} />
        </div>
      </div>
    </div>
  );
}